import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex h-full overflow-hidden" };
const _hoisted_2 = {
    class: "flex-shrink-0 hidden lg:flex lg:flex-col",
    style: { "width": "340px" }
};
const _hoisted_3 = { class: "relative flex-1 min-w-0" };
const _hoisted_4 = {
    key: 0,
    class: "flex flex-col h-full bg-white"
};
const _hoisted_5 = { class: "flex items-center justify-between px-4 py-4 border-b border-gray-200" };
const _hoisted_6 = { class: "text-base font-semibold text-gray-900" };
import { ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { IconPlus } from "@tabler/icons-vue";
import { getLoginUserId } from "@/plugins/shared";
import { useConversations } from "../composables/useConversations";
import { useConversationCreation } from "../composables/useConversationCreation";
import { useConversationFormData } from "../composables/useConversationFormData";
import { useConversationStore } from "../stores";
import { useAuthStore } from "@/domains/auth";
import ConversationSidebar from "./ConversationSidebar.vue";
import ConversationDrawer from "./ConversationDrawer.vue";
import ConversationPanel from "./ConversationPanel.vue";
import ConversationList from "./ConversationList.vue";
import NewConversationModal from "./NewConversationModal.vue";
// =============================================================================
// SETUP
// =============================================================================
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConversationPage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const store = useConversationStore();
        const authStore = useAuthStore();
        const { conversations, isLoading } = useConversations();
        const { createPreBooking, createPostBooking } = useConversationCreation();
        const { providers, services, itineraries, isLoading: isLoadingFormData, load: loadFormData, } = useConversationFormData();
        const currentUserId = String(getLoginUserId());
        // =============================================================================
        // ACTIVE CONVERSATION
        // =============================================================================
        const activeConversationId = ref(route.query.conversationId ?? undefined);
        watch(() => route.query.conversationId, (id) => {
            activeConversationId.value = id;
            if (id) {
                const conv = store.conversations.find((c) => c.id === id) ?? null;
                store.setActiveConversation(conv);
            }
            else {
                store.setActiveConversation(null);
            }
        });
        function handleSelect(conversation) {
            store.setActiveConversation(conversation);
            router.replace({ query: { conversationId: conversation.id } });
            drawerOpen.value = false;
        }
        function handleBack() {
            activeConversationId.value = undefined;
            store.setActiveConversation(null);
            router.replace({ query: {} });
        }
        // =============================================================================
        // RESPONSIVE
        // =============================================================================
        const drawerOpen = ref(false);
        const lgScreen = ref(window.innerWidth >= 1024);
        onMounted(() => {
            const observer = new ResizeObserver(() => {
                lgScreen.value = window.innerWidth >= 1024;
            });
            observer.observe(document.body);
            loadFormData({
                includeProviders: false,
                providerBookings: authStore.currentOrgType === "provider",
            });
        });
        // =============================================================================
        // NEW CONVERSATION FLOW (single modal)
        // =============================================================================
        const showNewConversationModal = ref(false);
        const isCreatingConversation = ref(false);
        function openNewConversationModal() {
            showNewConversationModal.value = true;
            loadFormData({
                includeProviders: false,
                providerBookings: authStore.currentOrgType === "provider",
                forceRefreshItineraries: authStore.currentOrgType === "concierge",
                forceRefreshServices: authStore.currentOrgType === "concierge",
            });
        }
        async function handleCreate(payload) {
            isCreatingConversation.value = true;
            try {
                if (payload.conversation_type === "PRE_BOOKING" &&
                    payload.serviceId) {
                    await createPreBooking({
                        serviceId: payload.serviceId,
                    });
                }
                else if (payload.conversation_type === "POST_BOOKING" &&
                    payload.itineraryId &&
                    (payload.bookingId || payload.slotId)) {
                    await createPostBooking({
                        serviceId: payload.serviceId ?? "",
                        providerId: payload.providerId ?? "",
                        itineraryId: payload.itineraryId,
                        bookingId: payload.bookingId,
                        slotId: payload.slotId,
                    });
                }
                showNewConversationModal.value = false;
            }
            finally {
                isCreatingConversation.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createVNode(ConversationSidebar, {
                        conversations: _unref(conversations),
                        "active-conversation-id": activeConversationId.value,
                        "is-loading": _unref(isLoading),
                        services: _unref(services),
                        itineraries: _unref(itineraries),
                        onSelect: handleSelect,
                        onNewConversation: openNewConversationModal
                    }, null, 8, ["conversations", "active-conversation-id", "is-loading", "services", "itineraries"])
                ]),
                _createVNode(ConversationDrawer, {
                    modelValue: drawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((drawerOpen).value = $event)),
                    conversations: _unref(conversations),
                    "active-conversation-id": activeConversationId.value,
                    "is-loading": _unref(isLoading),
                    services: _unref(services),
                    itineraries: _unref(itineraries),
                    onSelect: handleSelect,
                    onNewConversation: openNewConversationModal
                }, null, 8, ["modelValue", "conversations", "active-conversation-id", "is-loading", "services", "itineraries"]),
                _createElementVNode("div", _hoisted_3, [
                    (!activeConversationId.value && !lgScreen.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("h1", _hoisted_6, _toDisplayString(_ctx.$t("conversation.auto.conversaciones")), 1),
                                _createElementVNode("button", {
                                    onClick: openNewConversationModal,
                                    class: "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-eorange-400 text-white rounded-xl hover:bg-eorange-500"
                                }, [
                                    _createVNode(_unref(IconPlus), { class: "w-3.5 h-3.5" }),
                                    _createTextVNode(_toDisplayString(_ctx.$t("conversation.auto.nueva")), 1)
                                ])
                            ]),
                            _createVNode(ConversationList, {
                                conversations: _unref(conversations),
                                "is-loading": _unref(isLoading),
                                "current-user-id": _unref(currentUserId),
                                onSelect: handleSelect
                            }, null, 8, ["conversations", "is-loading", "current-user-id"])
                        ]))
                        : (_openBlock(), _createBlock(ConversationPanel, {
                            key: 1,
                            "conversation-id": activeConversationId.value,
                            "show-back": true,
                            class: "h-full",
                            onBack: handleBack
                        }, null, 8, ["conversation-id"]))
                ]),
                (showNewConversationModal.value)
                    ? (_openBlock(), _createBlock(NewConversationModal, {
                        key: 0,
                        providers: _unref(providers),
                        services: _unref(services),
                        itineraries: _unref(itineraries),
                        mode: _unref(authStore).currentOrgType,
                        "is-creating": isCreatingConversation.value || _unref(isLoadingFormData),
                        onCreate: handleCreate,
                        onCancel: _cache[1] || (_cache[1] = ($event) => (showNewConversationModal.value = false))
                    }, null, 8, ["providers", "services", "itineraries", "mode", "is-creating"]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
